import { createSlice, createEntityAdapter, createSelector } from '@reduxjs/toolkit'
import actionFactory from 'Shared/hooks/actionFactory'
import FieldMap from 'DashboardBuilder/FieldMap'
import { actions as questionsActionsF } from 'FlowBuilder/store/questions'

const adapter = createEntityAdapter()

const reducer = createSlice({
  name: 'dashboardFields',
  initialState: adapter.getInitialState(),
  reducers: {
    upsert: adapter.upsertOne,
    add: adapter.addOne,
    set: adapter.setOne,
    setAll: adapter.setAll,
    remove: adapter.removeOne,
    moveDigitOrder: (state, { payload }) => {
      // digit order is 1-based
      const { id, digitOrder: newDigitOrder } = payload
      const dashboardField = state.entities[id]
      const oldDigitOrder = dashboardField.digitOrder
      const dashboardFields = _.values(state.entities).filter(q => q.active) // only deal with active dashboardFields

      // for all dashboardFields on the old page, move them up
      dashboardFields.filter(df => df.digitOrder > oldDigitOrder).forEach(df => {
        state.entities[df.id].digitOrder--
      })

      // for all dashboardFields on the new page, move them down
      dashboardFields.filter(q => q.digitOrder >= newDigitOrder).forEach(q => {
        state.entities[q.id].digitOrder++
      })

      // update the dashboardField
      dashboardField.digitOrder = newDigitOrder
    }
  },
}).reducer

const actions = (dis, store, restClient) => {
  const questionsActions = questionsActionsF(dis, store, restClient)

  const factoryActions = actionFactory(dis, store, 'dashboardField', {}, {
    add: (dis, store, entity) => (data = {}) => {
      const id = uuidv4()
      const state = store.getState()
      const digitOrderMax = _.max(_.values(state.dashboardFields.entities).filter(a => a.active).map(q => q.digitOrder))
      const digitOrder = isNaN(digitOrderMax) ? 1 : digitOrderMax + 1
      dis({ type: `dashboardFields/upsert`, payload: { id, digitOrder, active: true, ...data } })
    },
    markInactive: (dis, store, entity) => (id) => {
      dis({ type: `dashboardFields/upsert`, payload: { id, active: false } })
      const questionId = store.getState().dashboardFields.entities[id].questionId
      questionsActions.markInactive(questionId)
    },
    chooseQuestionType: (dis, store, entity) => (id, questionType) => {
      const dashboardField = store.getState().dashboardFields.entities[id]

      if (dashboardField.questionId) {
        dis({ type: 'questions/upsert', payload: { id: dashboardField.questionId, questionType } })
      } else {
        const newQuestionId = uuidv4()
        let settings = {}
        if (questionType === 'score') {
          settings = { min: 1, max: 5 }
        }
        dis({ type: 'questions/add', payload: { id: newQuestionId, questionType, active: true, question: dashboardField.name, settings } })
        dis({ type: 'dashboardFields/upsert', payload: { id, questionId: newQuestionId } })
      }
    },
    setName: (dis, store, entity) => (id, name) => {
      dis({ type: `dashboardFields/upsert`, payload: { id, name } })
      // If this is a question, also set the question text
      const dashboardField = store.getState().dashboardFields.entities[id]
      if (dashboardField.source == 'answerable_question' && dashboardField.questionId) {
        questionsActions.updateQuestionTextContent({ questionId: dashboardField.questionId, language: 'en', key: 'name', value: name })
      }
    },
  })

  return {
    ...factoryActions,
  }
}

const selectInvalidDashboardFields = createSelector(
  (state) => state.dashboardFields.entities,
  (entities) => Object.values(entities).filter((field) => {
    if (!field.active) return false;
    const fieldMapItem = FieldMap.find((item) => item.source === field.source);
    if (!fieldMapItem) {
      return true; // If fieldMapItem is not found, must be invalid
    }
    const requiredFields = [...fieldMapItem.requiredFields, 'name'];
    const isInvalid = requiredFields.some((requiredField) => {
      const fieldValue = field[requiredField];
      return fieldValue === undefined || fieldValue === null || fieldValue === '';
    });
    return isInvalid; // Return true if any required field is empty, invalid
  })
);

export { reducer, actions, selectInvalidDashboardFields }
export default { reducer, actions, selectInvalidDashboardFields }
